import { useMemo, useState } from "react";
import { GitDiff, Warning, Download } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/* ============================================================
   BATCH DIFF VIEW · v2.80 + v2.81 diff-PDF export
   ------------------------------------------------------------
   Side-by-side comparison of two saved conductor batches (from
   the same project). Diffs every tag in the union of the two
   batches on the outputs a PE cares about during revision review:
   selected_size, recommended_breaker, voltage_drop_pct, and the
   mode-specific OCPD (motor branch / transformer / motor group).
   ============================================================ */
export default function BatchDiffView({ batchA, batchB, onClose }) {
  const rows = useMemo(() => buildDiffRows(batchA, batchB), [batchA, batchB]);
  const changed = rows.filter((r) => r.anyChanged).length;
  const onlyInOne = rows.filter((r) => r.presence !== "both").length;
  const [pdfBusy, setPdfBusy] = useState(false);

  const exportPdf = async () => {
    if (!batchA?.id || !batchB?.id) return;
    setPdfBusy(true);
    try {
      const res = await api.post(
        "/calculators/conductor-sizing/batches/diff-pdf",
        {
          batch_a_id: batchA.id,
          batch_b_id: batchB.id,
          engineer_name: batchB.engineer_name || batchA.engineer_name || "",
          engineer_pe_license: batchB.engineer_pe_license || batchA.engineer_pe_license || "",
        },
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const nameA = (batchA.project_name || "A").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 30);
      const nameB = (batchB.project_name || "B").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 30);
      a.download = `conductor_diff_${nameA}__vs__${nameB}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Revision-diff PDF downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Diff PDF failed");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="mt-4 border border-[#FFCC00]/30 bg-[#FFCC00]/[0.03] p-4"
      data-testid="batch-diff-view">
      <div className="flex items-center gap-2 mb-2">
        <GitDiff size={16} className="text-[#FFCC00]" />
        <div className="label-tag">// batch comparison</div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs font-mono text-gray-400" data-testid="batch-diff-summary">
            {rows.length} tag{rows.length !== 1 ? "s" : ""} · {changed} changed
            {onlyInOne > 0 && <span className="text-[#FF8A85]"> · {onlyInOne} only in one batch</span>}
          </span>
          <button onClick={exportPdf}
            disabled={pdfBusy || !batchA?.id || !batchB?.id}
            className="border border-[#FFCC00]/40 text-[#FFCC00] px-3 py-1 text-xs font-mono hover:bg-[#FFCC00]/10 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
            data-testid="batch-diff-export-pdf"
            title="Download a stampable one-pager summarising every change between the two batches.">
            <Download size={12} /> {pdfBusy ? "Rendering…" : "Diff PDF"}
          </button>
          {onClose && (
            <button onClick={onClose}
              className="text-gray-400 hover:text-white text-xs font-mono px-2 py-1 border border-white/10"
              data-testid="batch-diff-close">
              Close
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3 text-xs font-mono">
        <BatchHeader batch={batchA} accent="cyan" testid="batch-diff-a-header" />
        <BatchHeader batch={batchB} accent="yellow" testid="batch-diff-b-header" />
      </div>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead className="bg-[#0A0C10] text-gray-400">
            <tr className="border-b border-white/10">
              <Th>Tag</Th>
              <Th>Circuit type</Th>
              <Th>Load</Th>
              <Th colSpan={2}>Selected size</Th>
              <Th colSpan={2}>Breaker / OCPD</Th>
              <Th colSpan={2}>VD %</Th>
              <Th className="w-16">Δ</Th>
            </tr>
            <tr className="border-b border-white/10 text-[9px] text-gray-500">
              <Th></Th>
              <Th></Th>
              <Th>A → B</Th>
              <Th accent="cyan">A</Th>
              <Th accent="yellow">B</Th>
              <Th accent="cyan">A</Th>
              <Th accent="yellow">B</Th>
              <Th accent="cyan">A</Th>
              <Th accent="yellow">B</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center text-gray-500 py-4"
                    data-testid="batch-diff-empty">
                  No rows to compare — both batches are empty.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.tag}
                  className={`border-b border-white/5 ${r.anyChanged ? "bg-[#FFCC00]/[0.06]" : ""}`}
                  data-testid={`batch-diff-row-${r.tag}`}>
                <Td className="text-white">{r.tag}</Td>
                <Td>
                  <DiffCell a={r.ct.a} b={r.ct.b} changed={r.ct.changed} />
                </Td>
                <Td>
                  <DiffCell a={r.load.a} b={r.load.b} changed={r.load.changed} />
                </Td>
                <Td accent="cyan">{r.size.a ?? "—"}</Td>
                <Td accent="yellow" changed={r.size.changed}>{r.size.b ?? "—"}</Td>
                <Td accent="cyan">{r.ocpd.a ?? "—"}</Td>
                <Td accent="yellow" changed={r.ocpd.changed}>{r.ocpd.b ?? "—"}</Td>
                <Td accent="cyan">{r.vd.a ?? "—"}</Td>
                <Td accent="yellow" changed={r.vd.changed}>{r.vd.b ?? "—"}</Td>
                <Td className="text-center">
                  {r.presence === "onlyA" && <span className="text-[#00E5FF] text-[10px]" title="Only in A">A•</span>}
                  {r.presence === "onlyB" && <span className="text-[#FFCC00] text-[10px]" title="Only in B">•B</span>}
                  {r.presence === "both" && r.anyChanged && <Warning size={12} className="inline text-[#FFCC00]" />}
                  {r.presence === "both" && !r.anyChanged && <span className="text-gray-600">✓</span>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[10px] text-gray-500 font-mono">
        // <span className="text-[#00E5FF]">A</span> = older/first-picked batch · <span className="text-[#FFCC00]">B</span> = newer/second-picked batch. Yellow-highlighted rows are the ones a PE should sign off on after a revision.
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */
function BatchHeader({ batch, accent, testid }) {
  const color = accent === "cyan" ? "text-[#00E5FF]" : "text-[#FFCC00]";
  const border = accent === "cyan" ? "border-[#00E5FF]/40" : "border-[#FFCC00]/40";
  return (
    <div className={`border ${border} p-2`} data-testid={testid}>
      <div className={`text-[9px] uppercase tracking-wider ${color}`}>
        {accent === "cyan" ? "Batch A" : "Batch B"}
      </div>
      <div className="text-white text-xs mt-0.5 truncate">{batch.project_name || "—"}</div>
      <div className="text-[10px] text-gray-500">
        {batch.rows?.length ?? 0} rows · {batch.results?.filter((r) => r?.ok).length ?? 0} computed
        {batch.engineer_name && <span> · {batch.engineer_name}</span>}
      </div>
      <div className="text-[10px] text-gray-500">
        {batch.created_at ? new Date(batch.created_at).toLocaleString() : ""}
      </div>
    </div>
  );
}

const Th = ({ children, className = "", colSpan, accent }) => (
  <th colSpan={colSpan}
      className={`text-left font-normal px-2 py-1.5 text-[10px] uppercase tracking-wider ${accent === "cyan" ? "text-[#00E5FF]" : accent === "yellow" ? "text-[#FFCC00]" : ""} ${className}`}>
    {children}
  </th>
);

const Td = ({ children, className = "", accent, changed }) => {
  const accentColor = accent === "cyan" ? "text-[#00E5FF]" :
                      accent === "yellow" ? (changed ? "text-[#FFCC00] font-bold" : "text-[#FFCC00]") :
                      "text-white";
  return (
    <td className={`px-2 py-1.5 align-middle ${accentColor} ${className}`}>{children}</td>
  );
};

const DiffCell = ({ a, b, changed }) => {
  if (!changed) return <span>{a ?? "—"}</span>;
  return (
    <span className="text-[11px]">
      <span className="text-[#00E5FF]">{a ?? "—"}</span>
      <span className="text-gray-500 mx-1">→</span>
      <span className="text-[#FFCC00] font-bold">{b ?? "—"}</span>
    </span>
  );
};

/* ---------- Diff builder ---------- */
function buildDiffRows(a, b) {
  const aMap = tagMap(a?.rows || [], a?.results || []);
  const bMap = tagMap(b?.rows || [], b?.results || []);
  const tags = Array.from(new Set([...aMap.keys(), ...bMap.keys()]));
  return tags.map((tag) => {
    const A = aMap.get(tag);
    const B = bMap.get(tag);
    const presence = A && B ? "both" : A ? "onlyA" : "onlyB";
    const ct = { a: A?.circuit_type, b: B?.circuit_type };
    ct.changed = A && B && ct.a !== ct.b;
    const loadA = A ? `${A.load_value ?? A.load_amps ?? "—"} ${A.load_unit ?? "A"}` : "—";
    const loadB = B ? `${B.load_value ?? B.load_amps ?? "—"} ${B.load_unit ?? "A"}` : "—";
    const load = { a: loadA, b: loadB };
    load.changed = A && B && loadA !== loadB;
    const size = {
      a: A?.result?.selected_size,
      b: B?.result?.selected_size,
    };
    size.changed = A && B && size.a !== size.b;
    const ocpd = {
      a: pickOcpd(A?.result),
      b: pickOcpd(B?.result),
    };
    ocpd.changed = A && B && ocpd.a !== ocpd.b;
    const vd = {
      a: A?.result?.voltage_drop_pct != null ? `${A.result.voltage_drop_pct}%` : null,
      b: B?.result?.voltage_drop_pct != null ? `${B.result.voltage_drop_pct}%` : null,
    };
    vd.changed = A && B && vd.a !== vd.b;
    const anyChanged = ct.changed || load.changed || size.changed || ocpd.changed || vd.changed;
    return { tag, presence, ct, load, size, ocpd, vd, anyChanged };
  });
}

function pickOcpd(res) {
  if (!res) return null;
  if (res.motor_group?.supported) return `${res.motor_group.feeder_ocpd} A (mg feeder)`;
  if (res.motor_branch) return `${res.motor_branch.recommended_ocpd_amps} A (motor)`;
  if (res.transformer) {
    const sec = res.transformer.secondary?.recommended_ocpd_amps;
    const pri = res.transformer.primary?.recommended_ocpd_amps;
    return sec != null ? `${sec} A (tx sec)` : (pri != null ? `${pri} A (tx pri)` : null);
  }
  return res.recommended_breaker != null ? `${res.recommended_breaker} A` : null;
}

/* Map tag → { row-fields + result }.  We prefer the row.tag, and fall
   back to a synthetic key so untagged rows still compare in order. */
function tagMap(rows, results) {
  const m = new Map();
  rows.forEach((r, i) => {
    const key = (r.tag || "").trim() || `#${i + 1}`;
    const wrap = results[i];
    const result = wrap?.ok ? (wrap.result || null) : null;
    m.set(key, { ...r, result });
  });
  return m;
}
